import { InvestmentsPage } from "@gladis/domain-investments/ui";
import { Button } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { Page } from "../../../components/page.js";

export const Route = createFileRoute("/_authenticated/_shell/investments")({
  component: Investments,
});

/**
 * Investments.
 *
 * Composition only — the cards, the chart, the tables and the forms come from the investments
 * package, which owns what a holding is. This route decides where they appear and where a click on a
 * stock goes.
 *
 * ==========================================================================================
 * THIS PAGE COMPOSES NOTHING ELSE
 * ==========================================================================================
 *
 * It reads investments and no other financial context. Net worth is a question about four contexts at
 * once, and in GLADIS that question is answered on the Dashboard — the only surface permitted to
 * compose pillars.
 *
 * `investments-composes-nothing.test.ts` enforces it, because the dependency rules cannot: `apps/app`
 * may legitimately import several domain `/ui` packages, so nothing but that test stops a savings
 * import appearing here and becoming a second answer to "what are we worth".
 *
 * NO MEMBER LIST IS FETCHED HERE, unlike income and expenses. Nothing on this page is attributed to a
 * person: the portfolio is the household's, and per-member attribution is explicitly out of scope.
 */
function Investments(): ReactNode {
  const t = useT();
  const navigate = useNavigate();

  /*
   * THE DIALOG'S OPEN STATE LIVES HERE, because the button that opens it does.
   *
   * Every page's primary action sits at the end of its title row, in `Page.Title`'s `actions` slot —
   * and that row belongs to the route, not to the domain component. The page previously offered
   * `Add a stock` only inside its empty state, so the control vanished the moment the household
   * owned one stock and there was no way back to it.
   */
  const [adding, setAdding] = useState(false);

  return (
    <Page>
      <Page.Title
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setAdding(true);
            }}
          >
            {t("inv.page.addStock")}
          </Button>
        }
      >
        {t("nav.investments")}
      </Page.Title>
      <Page.Body>
        <div className="col-span-full flex flex-col gap-10">
          <InvestmentsPage
            adding={adding}
            onAddingChange={setAdding}
            onOpenStock={(stockId) => {
              void navigate({ to: "/investments/$stockId", params: { stockId } });
            }}
          />
        </div>
      </Page.Body>
    </Page>
  );
}
